import React from "react";
import { Card } from "./Card";
import { cn } from "../../libs/utils";

/**
 * Generic responsive table: a real `<table>` at `md`+ and a stacked card
 * list below it, with shared loading/empty states and header styling.
 * Both the Batch table and the Dispatch History table render through this
 * so the table chrome (borders, header background, row hover, spacing)
 * only needs to be defined once.
 *
 * @param {{key: string, header: string, render: (row) => React.ReactNode, className?: string}[]} columns
 * @param {(row) => React.ReactNode} renderCard - mobile card renderer for one row
 */
const DataTable = ({
  columns,
  rows,
  rowKey = "uid",
  isLoading,
  loadingLabel = "Loading…",
  noDataLabel = "No records found",
  onRowClick,
  renderCard,
  minWidth = 760,
}) => {
  if (isLoading) {
    return (
      <Card className="p-10 text-center text-sm text-slate-400" role="status">
        {loadingLabel}
      </Card>
    );
  }

  if (!rows.length) {
    return <Card className="p-10 text-center text-sm text-slate-400">{noDataLabel}</Card>;
  }

  return (
    <>
      <Card className="hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" style={{ minWidth }}>
            <thead>
              <tr className="bg-lightSecondary/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                {columns.map((col) => (
                  <th key={col.key} className="px-5 py-3">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row[rowKey]}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-slate-100 transition-colors last:border-0",
                    onRowClick && "cursor-pointer hover:bg-slate-50"
                  )}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn("px-5 py-3 text-slate-600", col.className)}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {renderCard && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:hidden">
          {rows.map((row) => (
            <React.Fragment key={row[rowKey]}>{renderCard(row)}</React.Fragment>
          ))}
        </div>
      )}
    </>
  );
};

export default DataTable;
